import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { siteConfig } from '../../data/config';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Phone, Menu, Plane, Car, MapPin, HelpCircle, DollarSign, Mail } from 'lucide-react';

const Header = () => {
  const { language, setLanguage, t, getRoute } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { basePath: '/', label: t('nav.home'), icon: Car },
    { basePath: '/airport', label: t('nav.airport'), icon: Plane },
    { basePath: '/services', label: t('nav.services'), icon: Car },
    { basePath: '/fares', label: t('nav.fares'), icon: DollarSign },
    { basePath: '/areas', label: t('nav.areas'), icon: MapPin },
    { basePath: '/faq', label: t('nav.faq'), icon: HelpCircle },
    { basePath: '/contact', label: t('nav.contact'), icon: Mail },
  ];

  const isActive = (basePath) => {
    const route = getRoute(basePath);
    if (basePath === '/') {
      return location.pathname === `/${language}` || location.pathname === '/';
    }
    return location.pathname === route;
  };

  // Handle language switch with navigation
  const handleLanguageSwitch = () => {
    const newLang = language === 'fr' ? 'en' : 'fr';
    setLanguage(newLang);
    
    // Navigate to equivalent page in new language
    const routeMappings = {
      fr: {
        '/fr': '/en',
        '/fr/aeroport-yul': '/en/yul-airport',
        '/fr/services': '/en/services',
        '/fr/tarifs': '/en/fares',
        '/fr/zones-desservies': '/en/service-areas',
        '/fr/faq': '/en/faq',
        '/fr/contact': '/en/contact',
        '/fr/confidentialite': '/en/privacy-policy',
        '/fr/conditions-utilisation': '/en/terms-of-use',
        '/fr/avertissement': '/en/disclaimer',
      },
      en: {
        '/en': '/fr',
        '/en/yul-airport': '/fr/aeroport-yul',
        '/en/services': '/fr/services',
        '/en/fares': '/fr/tarifs',
        '/en/service-areas': '/fr/zones-desservies',
        '/en/faq': '/fr/faq',
        '/en/contact': '/fr/contact',
        '/en/privacy-policy': '/fr/confidentialite',
        '/en/terms-of-use': '/fr/conditions-utilisation',
        '/en/disclaimer': '/fr/avertissement',
      },
    };
    
    const newPath = routeMappings[language][location.pathname];
    if (newPath) {
      navigate(newPath);
    } else {
      navigate(`/${newLang}`);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to={getRoute('/')} className="flex items-center" data-testid="logo-link">
            <img 
              src="/images/mtl-taxi-logo-electric-suv-montreal.png"
              alt="MTL.TAXI"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.basePath}
                to={getRoute(link.basePath)}
                data-testid={`nav-${link.basePath.replace('/', '') || 'home'}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.basePath)
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-foreground'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Switcher */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLanguageSwitch}
              data-testid="language-switch"
              className="font-semibold text-sm"
            >
              {language === 'fr' ? 'EN' : 'FR'}
            </Button>

            {/* Phone - Desktop */}
            <a
              href={siteConfig.phoneTel}
              className="hidden md:flex items-center gap-2 text-primary font-semibold"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              <span>{siteConfig.phone}</span>
            </a>

            {/* Book Button - Desktop */}
            <Button
              asChild
              className="hidden md:flex bg-accent text-accent-foreground hover:bg-accent/90 font-bold shadow-md"
              data-testid="header-book-btn"
            >
              <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                {t('hero.bookOnline')}
              </a>
            </Button>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-heading font-bold text-xl">{siteConfig.businessName}</span>
                  </div>
                  
                  <nav className="flex flex-col gap-1">
                    {navLinks.map((link) => {
                      const Icon = link.icon;
                      return (
                        <Link
                          key={link.basePath}
                          to={getRoute(link.basePath)}
                          onClick={() => setIsOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                            isActive(link.basePath)
                              ? 'bg-primary/10 text-primary'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          {link.label}
                        </Link>
                      );
                    })}
                  </nav>

                  <div className="mt-auto pt-6 border-t border-gray-200">
                    <a
                      href={siteConfig.phoneTel}
                      className="flex items-center gap-3 px-4 py-3 text-primary font-semibold"
                    >
                      <Phone className="w-5 h-5" />
                      {siteConfig.phone}
                    </a>
                    <Button
                      asChild
                      className="w-full mt-4 bg-accent text-accent-foreground hover:bg-accent/90 font-bold h-12"
                    >
                      <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                        {t('hero.bookOnline')}
                      </a>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
